/**
 * S1b 상회칠판 (옛 상회공지 · 상회게시판, SPEC_CHANGE_v1.3 17장 C-22, 시안 09 · 08 6장 B1~B6, D52).
 *
 *   상단 바(인생상회 워드마크 · ☰) → 알약 [오늘 칠판][고민] → 오늘 칠판 | 고민 → 하단 탭
 *
 * - 오늘 칠판: 칠판 머리(한국 날짜 · 요일 소개) → 적힌 칸(시각 순) → 오늘 남은 칸 → 지난 칠판(최근 14일, 「더 보기」)
 *   새벽(아직 칸이 없음)이면 머리 안 빈 칠판 문구 + 어제 칠판을 펼친 채
 * - 날짜는 서버의 today_kst 만 씁니다. 1분마다 한국 날짜를 보고 바뀌면 다시 읽으며 머리를 지우개로 지웁니다(MO4).
 *   예정 칸의 시각이 되거나 화면에 돌아오면 다시 읽고, 새 칸은 분필로 쓰듯 드러납니다(MO5)
 * - 고민: 「고민 올리기」 → 고민 글쓰기(종류 고정) → 고민 글. 예전 단골 글은 「내가 쓴 글」로 남습니다
 * - 1:1 대화와 별채대화 말은 여기에 나오지 않습니다 (CLAUDE.md). 서버가 보내지 않습니다
 * - 고른 알약은 이 기기에만 기억합니다(localStorage)
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { HomeTopBar } from '../../components/TopBar.jsx';
import { Button } from '../../components/Button.jsx';
import { EvidenceSheet } from '../../components/EvidenceSheet.jsx';
import { BirthSheet } from '../../components/birth/BirthSheet.jsx';
import { useGoBack } from '../../hooks/useGoBack.js';
import { errorMessageOf, isAbortError } from '../../api/client.js';
import { track } from '../../api/track.js';
import * as boardApi from '../../api/board.js';
import { useSession } from '../../stores/session.js';
import { useNav } from '../../stores/nav.js';
import { ChalkHeader, ChalkSlot, PastBoards, PlannedSlots } from './Chalkboard.jsx';
import { WorryTab } from './WorryTab.jsx';
import { PostComposer } from './PostComposer.jsx';
import { hasDueSchedule, seoulToday, toFeedPost, toggledReactions } from './boardFeed.js';
import { usePendingPosts } from './usePendingPosts.js';

const TAB_KEY = 'insaeng-board-tab';
const TABS = [
    { value: 'today', label: '오늘 칠판' },
    { value: 'worry', label: '고민' },
];
const CLOCK_TICK_MS = 60 * 1000;
const NOW_TICK_MS = 30 * 1000;

function readTab() {
    try {
        return localStorage.getItem(TAB_KEY) === 'worry' ? 'worry' : 'today';
    } catch {
        return 'today';
    }
}

function saveTab(value) {
    try {
        localStorage.setItem(TAB_KEY, value);
    } catch {
        // 이 기기에 기억하지 못해도 알약은 됩니다
    }
}

export function BoardPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { evidenceKey } = useParams();
    const closeSheet = useGoBack('/board');
    const displayName = useSession((state) => state.user?.displayName) || '단골';
    const markNoticeSeen = useNav((state) => state.markNoticeSeen);

    const [tab, setTab] = useState(() => (location.state?.tab === 'worry' ? 'worry' : readTab()));
    const [board, setBoard] = useState({ status: 'loading', data: null, error: null });
    const [erasing, setErasing] = useState(null);
    const [pastOpen, setPastOpen] = useState({});
    const [pastMore, setPastMore] = useState({ loading: false });
    const [worries, setWorries] = useState({ posts: null, error: null });
    const [now, setNow] = useState(() => Date.now());
    const [birthSheetOpen, setBirthSheetOpen] = useState(false);
    const [composer, setComposer] = useState(() => ({
        open: Boolean(location.state?.compose),
        text: location.state?.prefill ?? '',
    }));
    const [checkInPostId, setCheckInPostId] = useState(null);
    const [retryingId, setRetryingId] = useState(null);
    const [actionError, setActionError] = useState(null);
    const [collapsedSafety, setCollapsedSafety] = useState(() => new Set());
    // 처음 읽은 뒤에 새로 나타난 칸만 분필로 쓰듯 드러냅니다(MO5)
    const seenSlotsRef = useRef(null);
    const [freshSlots, setFreshSlots] = useState(() => new Set());

    const loadBoard = useCallback(async (signal, { erase = null } = {}) => {
        try {
            const data = await boardApi.fetchBoard({ signal });
            const keys = data.items.map((slot) => slot.key);
            if (seenSlotsRef.current) {
                setFreshSlots(new Set(keys.filter((key) => !seenSlotsRef.current.has(key))));
            }
            seenSlotsRef.current = new Set(keys);
            setBoard({ status: 'ready', data, error: null });
            setErasing(erase);
            // 새벽(아직 적힌 칸이 없음)이면 어제 칠판을 펼쳐 둡니다(B2)
            const yesterday = data.past?.[0];
            if (!data.items.length && yesterday) {
                setPastOpen((previous) => (previous[yesterday.date] ? previous : { [yesterday.date]: 'loading' }));
                boardApi
                    .fetchBoard({ day: yesterday.date })
                    .then((day) => setPastOpen((previous) => ({ ...previous, [yesterday.date]: day.items })))
                    .catch(() => setPastOpen((previous) => ({ ...previous, [yesterday.date]: 'error' })));
            }
        } catch (error) {
            if (isAbortError(error)) {
                return;
            }
            setBoard((previous) => ({
                ...previous,
                status: previous.data ? 'ready' : 'error',
                error: errorMessageOf(error, '상회칠판을 불러오지 못했어요.'),
            }));
        }
    }, []);

    const loadWorries = useCallback(async (signal) => {
        try {
            const data = await boardApi.fetchWorries({ signal });
            setWorries({ posts: data.posts.map(toFeedPost), error: null });
        } catch (error) {
            if (!isAbortError(error)) {
                setWorries((previous) => ({
                    ...previous,
                    error: errorMessageOf(error, '고민 칸을 불러오지 못했어요.'),
                }));
            }
        }
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        loadBoard(controller.signal);
        loadWorries(controller.signal);
        track('board_open', {});
        return () => controller.abort();
    }, [loadBoard, loadWorries]);

    const todayKst = board.data?.todayKst ?? null;
    const schedule = board.data?.schedule ?? [];

    // 상회칠판을 열었으니 오늘 매일 콘텐츠는 본 것으로 (하단 탭 점)
    useEffect(() => {
        if (todayKst) {
            markNoticeSeen(todayKst);
        }
    }, [todayKst, markNoticeSeen]);

    // 1분마다 한국 날짜를 보고(바뀌면 지우개 MO4), 예정 칸의 시각이 됐으면 다시 읽습니다(MO5)
    useEffect(() => {
        if (!todayKst) {
            return undefined;
        }
        const timer = setInterval(() => {
            const kst = seoulToday();
            if (kst !== todayKst) {
                setPastOpen({});
                loadBoard(undefined, { erase: board.data?.dateLabel ?? null });
            } else if (hasDueSchedule(schedule)) {
                loadBoard();
            }
        }, CLOCK_TICK_MS);
        return () => clearInterval(timer);
    }, [todayKst, schedule, board.data?.dateLabel, loadBoard]);

    // 「방금」 밑줄이 10분 뒤 사라지게 지금 시각을 갱신합니다
    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), NOW_TICK_MS);
        return () => clearInterval(timer);
    }, []);

    // 화면에 돌아오면 다시 읽습니다
    useEffect(() => {
        const onVisible = () => {
            if (document.visibilityState === 'visible') {
                loadBoard();
                loadWorries();
            }
        };
        document.addEventListener('visibilitychange', onVisible);
        return () => document.removeEventListener('visibilitychange', onVisible);
    }, [loadBoard, loadWorries]);

    // 예정 칸을 보여 준 날 한 번
    const scheduleSeenRef = useRef(null);
    useEffect(() => {
        if (todayKst && schedule.length && scheduleSeenRef.current !== todayKst) {
            scheduleSeenRef.current = todayKst;
            track('board_schedule_seen', { count: schedule.length });
        }
    }, [todayKst, schedule.length]);

    const updatePost = useCallback((post) => {
        setWorries((previous) => ({
            ...previous,
            posts: (previous.posts ?? []).map((entry) => (entry.postsId === post.postsId ? post : entry)),
        }));
    }, []);

    const markPostStalled = useCallback((postsId) => {
        setWorries((previous) => ({
            ...previous,
            posts: (previous.posts ?? []).map((post) =>
                post.postsId === postsId ? { ...post, status: 'stalled' } : post
            ),
        }));
    }, []);

    usePendingPosts(worries.posts ?? [], updatePost, markPostStalled);

    function chooseTab(value) {
        setTab(value);
        saveTab(value);
        track('board_tab', { tab: value });
    }

    async function togglePast(date) {
        if (pastOpen[date]) {
            setPastOpen((previous) => {
                const next = { ...previous };
                delete next[date];
                return next;
            });
            return;
        }
        track('board_past_open', {});
        setPastOpen((previous) => ({ ...previous, [date]: 'loading' }));
        try {
            const day = await boardApi.fetchBoard({ day: date });
            setPastOpen((previous) => ({ ...previous, [date]: day.items }));
        } catch {
            setPastOpen((previous) => ({ ...previous, [date]: 'error' }));
        }
    }

    async function loadMorePast() {
        if (!board.data?.pastNextBefore || pastMore.loading) {
            return;
        }
        setPastMore({ loading: true });
        try {
            const more = await boardApi.fetchBoard({ pastBefore: board.data.pastNextBefore });
            setBoard((previous) => ({
                ...previous,
                data: {
                    ...previous.data,
                    past: [...previous.data.past, ...more.past],
                    pastNextBefore: more.pastNextBefore,
                },
            }));
        } catch (error) {
            setActionError(errorMessageOf(error, '지난 칠판을 더 불러오지 못했어요.'));
        } finally {
            setPastMore({ loading: false });
        }
    }

    /** 리액션은 먼저 화면에 반영하고, 서버 답으로 맞춥니다. 실패하면 되돌립니다 */
    async function handleReact(item, kind) {
        const applyReactions = (reactions) => {
            setBoard((previous) => ({
                ...previous,
                data: {
                    ...previous.data,
                    items: previous.data.items.map((slot) =>
                        slot.type === 'daily' && slot.item.dailyItemsId === item.dailyItemsId
                            ? { ...slot, item: { ...slot.item, reactions } }
                            : slot
                    ),
                },
            }));
        };
        const before = item.reactions;
        applyReactions(toggledReactions(before, kind));
        try {
            const { reactions } = await boardApi.toggleReaction(item.dailyItemsId, kind);
            applyReactions(reactions);
        } catch (error) {
            applyReactions(before);
            setActionError(errorMessageOf(error, '리액션을 남기지 못했어요.'));
        }
    }

    async function handleRetry(postsId) {
        setRetryingId(postsId);
        setActionError(null);
        try {
            const { post } = await boardApi.retryPost(postsId);
            updatePost(toFeedPost(post));
        } catch (error) {
            setActionError(errorMessageOf(error, '다시 받지 못했어요. 잠시 후 다시 시도해 주세요.'));
        } finally {
            setRetryingId(null);
        }
    }

    /** 애매한 위기 신호의 글: 이모가 확인 질문으로 먼저 잇는 1:1 로 갑니다 (D17) */
    async function handleCheckIn(postsId) {
        setCheckInPostId(postsId);
        try {
            const { memberKey } = await boardApi.startCheckIn(postsId);
            navigate(`/chat/${memberKey}`);
        } catch (error) {
            setActionError(errorMessageOf(error, '이모한테 가지 못했어요. 잠시 후 다시 시도해 주세요.'));
        } finally {
            setCheckInPostId(null);
        }
    }

    function toggleSafety(postsId, collapsed) {
        setCollapsedSafety((previous) => {
            const next = new Set(previous);
            if (collapsed) {
                next.add(postsId);
            } else {
                next.delete(postsId);
            }
            return next;
        });
    }

    function openPost(post) {
        navigate(post.kind === 'worry' ? `/worry/${post.postsId}` : `/board/posts/${post.postsId}`, {
            state: { from: 'board' },
        });
    }

    const data = board.data;
    const boardContext = { displayName, hasBirth: data?.hasBirth ?? true, canStartDay: () => false };

    return (
        <>
            <HomeTopBar label="상회칠판" />
            <div role="tablist" aria-label="상회칠판 칸" className="flex shrink-0 gap-2 px-gutter-home pt-3">
                {TABS.map((option) => {
                    const selected = option.value === tab;
                    return (
                        <button
                            key={option.value}
                            type="button"
                            role="tab"
                            aria-selected={selected}
                            onClick={() => chooseTab(option.value)}
                            className={`h-9 rounded-[18px] px-4 text-[14px] font-semibold ${
                                selected
                                    ? 'bg-brand text-on-brand'
                                    : 'border border-hairline bg-surface text-text-primary'
                            }`}
                        >
                            {option.label}
                        </button>
                    );
                })}
            </div>
            <main className="flex min-h-0 flex-1 flex-col overflow-y-auto px-gutter-home pb-8">
                {tab === 'today' ? (
                    <>
                        {board.status === 'loading' ? (
                            <p className="m-0 py-10 text-center text-caption text-text-secondary">
                                상회칠판을 펼치는 중이에요.
                            </p>
                        ) : null}
                        {board.status === 'error' ? (
                            <div className="flex flex-col items-center gap-3 py-10 text-center">
                                <p role="alert" className="m-0 text-caption text-error">
                                    {board.error}
                                </p>
                                <Button variant="secondary" size="md" onClick={() => loadBoard()}>
                                    다시 불러오기
                                </Button>
                            </div>
                        ) : null}
                        {data ? (
                            <>
                                <ChalkHeader
                                    dateLabel={data.dateLabel}
                                    weekdayLine={data.weekdayLine}
                                    calendarLine={data.calendarLine ?? null}
                                    shopLine={data.shopLine ?? null}
                                    empty={!data.items.length}
                                    erasing={erasing}
                                />
                                {data.items.map((slot) => (
                                    <ChalkSlot
                                        key={slot.key}
                                        slot={slot}
                                        now={now}
                                        fresh={freshSlots.has(slot.key)}
                                        board={boardContext}
                                        onReact={handleReact}
                                        onAskBirth={() => setBirthSheetOpen(true)}
                                        onOpenEvidence={(key) => navigate(`/board/evidence/${key}`)}
                                        onOpenMemberPost={(id) => navigate(`/board/member-posts/${id}`)}
                                    />
                                ))}
                                <PlannedSlots schedule={schedule} />
                                <PastBoards
                                    past={data.past}
                                    open={pastOpen}
                                    onToggle={togglePast}
                                    onMore={data.pastNextBefore ? loadMorePast : null}
                                    loadingMore={pastMore.loading}
                                />
                            </>
                        ) : null}
                    </>
                ) : (
                    <WorryTab
                        posts={worries.posts}
                        today={todayKst}
                        error={worries.error}
                        displayName={displayName}
                        checkInPostId={checkInPostId}
                        retryingId={retryingId}
                        collapsedSafety={collapsedSafety}
                        onWrite={() => setComposer({ open: true, text: '' })}
                        onOpen={openPost}
                        onRetry={handleRetry}
                        onCheckIn={handleCheckIn}
                        onToggleSafety={toggleSafety}
                        onReload={() => loadWorries()}
                    />
                )}
                {actionError ? (
                    <p role="alert" className="m-0 py-2 text-center text-caption text-error">
                        {actionError}
                    </p>
                ) : null}
            </main>
            <PostComposer
                open={composer.open}
                initialText={composer.text}
                onClose={() => setComposer({ open: false, text: '' })}
                onPosted={(post) => {
                    setComposer({ open: false, text: '' });
                    loadWorries();
                    openPost(post);
                }}
            />
            <EvidenceSheet evidenceKey={evidenceKey} onClose={closeSheet} />
            <BirthSheet
                open={birthSheetOpen}
                onClose={() => setBirthSheetOpen(false)}
                onSaved={() => {
                    setBirthSheetOpen(false);
                    loadBoard();
                }}
            />
        </>
    );
}
